/**
 * A comprehensive enumeration of potential artifact or document categories.
 * Used to classify legal documents and artifacts by their content and purpose.
 */
export enum ArtifactTag {
  POLICE_REPORT_AND_EVIDENCE = 'Police Report and Evidence',
  INSURANCE_INFORMATION = 'Insurance Information',
  MEDICAL_RECORDS = 'Medical Records',
  BILLS_AND_LIENS = 'Bills and Liens',
  CLIENT_CORRESPONDENCE = 'Client Correspondence',
  UNCLEAR = 'Unclear',
}

/**
 * Types of artifacts in the system.
 * Each artifact type represents a different data source or document format
 * that can be processed and analyzed within the system.
 */
export enum ArtifactType {
  TRANSCRIPT = 'TRANSCRIPT',
  AUDIO_CONVERSATION = 'AUDIO_CONVERSATION',
  DOCUMENT = 'DOCUMENT',
  EMAIL = 'EMAIL',
  EMAIL_ATTACHMENT = 'EMAIL_ATTACHMENT',
  INTAKE_NOTE = 'INTAKE_NOTE',
  MESSAGE = 'MESSAGE',
  MESSAGE_ATTACHMENT = 'MESSAGE_ATTACHMENT',
  CONVERSATION = 'CONVERSATION',
  BLOB = 'BLOB',
  STAFF_NOTE = 'STAFF_NOTE',
}
/**
 * Status of an artifact digest.
 * Represents the current state of processing for the digest.
 */
export enum ArtifactDigestStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}
/**
 * Base model interface with common fields for all entities.
 */
export interface BaseModel {
  id: string; // Unique identifier
  created_at: string; // Creation timestamp
  modified_at: string; // Last modification timestamp
}

/**
 * References to entities in the artifact.
 * Includes reference numbers, contact information, and identifiers mentioned in the artifact.
 * Examples: phone numbers, emails, policy numbers, claim numbers, etc.
 */
export interface ArtifactReference extends BaseModel {
  description: string; // Description or context of the reference
  value: string; // The actual reference value (e.g., +12019567542)
  type: string; // Type of reference (phone, email, policy number, etc.)
}

/**
 * Facts extracted from the artifact.
 * Objective, verifiable statements derived from the content that are pertinent to the legal case.
 * Facts should be fully qualified, maximally detailed and complete statements.
 */
export interface ArtifactFact extends BaseModel {
  statement: string; // The fact extracted from the artifact
  attribution: string; // Source or attribution of the fact
  supporting_excerpts: string[]; // Direct quotes from the text that support the fact
}

/**
 * A comprehensive summary of an artifact's contents.
 * Provides structured information about the document including categorization,
 * information density, and relevance to the matter.
 */
export interface ArtifactDigest extends BaseModel {
  artifact_id: string; // ID of the artifact the digest is for
  status: ArtifactDigestStatus; // Status of the artifact digest
  title: string; // Concise and meaningful title of the artifact
  subtitle: string | null; // A subtitle for the artifact
  brief: string | null; // 2-3 sentence explainer of the content within the artifact
  work_log_entry: string | null; // Existence of the artifact expressed as an internal work log entry
  primary_tag: ArtifactTag; // Best tag that describes the artifact
  secondary_tags: ArtifactTag[] | null; // Additional tags that describe the artifact
  source: string | null; // Inferred source or author of the artifact
  information_score: number | null; // How much information is conveyed (0.0 to 1.0)
  client_name: string | null; // Name of the client the artifact is likely related to
  confidence: number | null; // Confidence score for client identification (0.0 to 1.0)
  long_form_description: string; // Exhaustively and objectively describe the content
  three_line_summary: string; // 1-3 sentences that describe the content
  one_line_summary: string; // One sentence that summarizes the content
  file_name: string; // Information dense, briefest possible file name for the artifact - shorthanded so it can be used in a file system
}
